import type { DndClass, PackEntry, PackSubclass, RawContentPack, Species } from '@/entities/content/types/content'

/** An entity that at least carries a string `id` (every resolved content entity does). */
type Identified = { id: string; name?: string }

/** Turn a display name into a stable, url-ish id: "My Barbarian" → "my-barbarian". */
export function slugify(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/** True when a pack entry is an extension (`{ base: "<id>", ... }`) rather than a full entity. */
function isExtension(entry: unknown): entry is Record<string, unknown> & { base: string } {
  return (
    !!entry &&
    typeof entry === 'object' &&
    typeof (entry as Record<string, unknown>).base === 'string'
  )
}

/** Loose match for a name or id: case, spacing, punctuation and apostrophe style ignored. */
export const matchKey = (s: string) => slugify(s.replace(/['’]/g, ''))

/** The names a `"-field"` entry can be addressed by: a plain string, or an entity's id or name. */
function namesOf(entry: unknown): string[] {
  if (typeof entry === 'string') return [entry]
  if (!entry || typeof entry !== 'object') return []
  const e = entry as { id?: unknown; name?: unknown }
  return [e.id, e.name].filter((v): v is string => typeof v === 'string')
}

/** Patch keys apply in a fixed order, whatever the JSON's: replace, then remove, then append. */
const keyRank = (key: string) => (key.startsWith('-') ? 1 : key.startsWith('+') ? 2 : 0)

/**
 * Clone `base` and apply the override `patch` on top.
 * - `base` key is consumed: it is not copied verbatim, but its value is recorded
 *   on the result as `baseId` so consumers can trace the extension lineage (e.g.
 *   a custom subclass that took a new id still draws on its base class spell list).
 * - a `"+field"` key appends its array to the inherited `field` array.
 * - a `"-field"` key drops inherited entries named by id or name (`"-traits": ["Adrenaline Rush"]`),
 *   so a pack can shed one trait and add its own instead of restating the whole array. A name that
 *   matches nothing is reported on `errors` rather than ignored: it's a typo, or the base moved on.
 * - any other key replaces the inherited value.
 * - if the patch sets no `id`, one is derived from the (possibly overridden) `name`.
 *
 * Keys apply in `keyRank` order, so `-field` always sees the inherited array and `+field` the
 * already-pruned one, however the author ordered them.
 */
export function applyExtension<T extends Identified>(
  base: T,
  patch: Record<string, unknown>,
  errors: string[] = [],
): T {
  const result = structuredClone(base) as Record<string, unknown>

  for (const [key, value] of Object.entries(patch).sort(([a], [b]) => keyRank(a) - keyRank(b))) {
    if (key === 'base') continue
    if (key.startsWith('+')) {
      const field = key.slice(1)
      const existing = Array.isArray(result[field]) ? (result[field] as unknown[]) : []
      const added = Array.isArray(value) ? value : [value]
      result[field] = [...existing, ...added]
    } else if (key.startsWith('-')) {
      const field = key.slice(1)
      const existing = Array.isArray(result[field]) ? (result[field] as unknown[]) : []
      const wanted = Array.isArray(value) ? value : [value]
      let kept = existing
      for (const name of wanted) {
        if (typeof name !== 'string') {
          errors.push(`Extension of '${patch.base}': "${key}" takes the names or ids to remove, as strings.`)
          continue
        }
        const next = kept.filter((e) => !namesOf(e).some((n) => matchKey(n) === matchKey(name)))
        if (next.length === kept.length) {
          errors.push(`Extension of '${patch.base}': "${key}" found no '${name}' to remove.`)
        }
        kept = next
      }
      result[field] = kept
    } else {
      result[key] = value
    }
  }

  if (patch.id === undefined && typeof result.name === 'string') {
    result.id = slugify(result.name)
  }

  // Record this entity's immediate base so its lineage can be walked. (Harmless
  // for entity types that don't read it; classes use it for spell-list lookup.)
  result.baseId = patch.base

  return result as T
}

/**
 * Resolve a list of pack entries (full entities and/or extensions) into concrete
 * entities. Plain entities seed an id→entity map; extensions are then resolved
 * iteratively so an extension may itself base off another extension (chains).
 * Problems are pushed onto `errors` (unknown base id, or an extension with no new identity).
 * First-seen order is preserved. An entry reusing an id replaces the earlier one in its
 * place (a later pack wins, as for subclasses): a pack that rewrites an SRD spell yields one
 * spell, never two sharing an id.
 */
export function resolveEntities<T extends Identified>(
  entries: PackEntry<T>[],
  errors: string[],
): T[] {
  const byId = new Map<string, T>()
  // Output slots keep original ordering; extensions fill in once resolvable.
  const slots: (T | null)[] = entries.map((entry) => {
    if (isExtension(entry)) return null
    const e = entry as T
    if (e.id) byId.set(e.id, e)
    return e
  })

  // Indices of still-unresolved extensions.
  let pending = entries
    .map((entry, i) => (isExtension(entry) ? i : -1))
    .filter((i) => i >= 0)

  let progressed = true
  while (pending.length && progressed) {
    progressed = false
    const stillPending: number[] = []
    for (const i of pending) {
      const patch = entries[i] as Record<string, unknown> & { base: string }
      const base = byId.get(patch.base)
      if (!base) {
        stillPending.push(i)
        continue
      }
      if (patch.id === undefined && patch.name === undefined) {
        errors.push(
          `Extension of '${patch.base}' must set a new "name" or "id" so it doesn't collide with its base.`,
        )
        progressed = true // consume it (don't loop forever)
        continue
      }
      const resolved = applyExtension(base, patch, errors)
      byId.set(resolved.id, resolved)
      slots[i] = resolved
      progressed = true
    }
    pending = stillPending
  }

  // Anything left could not find its base.
  for (const i of pending) {
    const patch = entries[i] as Record<string, unknown> & { base: string }
    errors.push(`Unknown base '${patch.base}'.`)
  }

  const out: T[] = []
  const at = new Map<string, number>()
  for (const e of slots) {
    if (!e) continue
    const i = e.id ? at.get(e.id) : undefined
    if (i === undefined) {
      if (e.id) at.set(e.id, out.length)
      out.push(e)
    } else out[i] = e
  }
  return out
}

/**
 * Mark species whose lineages all came from a pack. A species' own definition (the
 * entry without a `base`) declaring no `subspecies` means a character needs no
 * lineage; a pack that later extends it with one mustn't take the plain species
 * away, so the resolved species is flagged `lineageOptional`. A pack redefining
 * the species outright *with* lineages clears the flag again: those are its own
 * complete set.
 */
export function withOptionalLineages(species: Species[], entries: PackEntry<Species>[]): Species[] {
  const shippedNone = new Set<string>()
  for (const entry of entries) {
    if (isExtension(entry)) continue
    const e = entry as Partial<Species>
    const id = e.id ?? slugify(e.name ?? '')
    if (!id) continue
    if (e.subspecies?.length) shippedNone.delete(id)
    else shippedNone.add(id)
  }
  return species.map((s) =>
    s.subspecies.length > 0 && shippedNone.has(s.id) ? { ...s, lineageOptional: true } : s,
  )
}

/**
 * Add packs' subclasses to their classes: each joins the options of the class's
 * `subclass` choice, replacing an option with the same id (a later pack wins). A
 * missing `id` is slugified from `name`. Returns new class objects; problems (an
 * unknown class, a class with no subclass choice) are pushed onto `errors`.
 */
export function attachSubclasses(classes: DndClass[], subclasses: PackSubclass[], errors: string[]): DndClass[] {
  if (!subclasses.length) return classes
  const out = classes.map((c) => ({ ...c, levels: c.levels?.map((l) => ({ ...l, choices: l.choices.map((ch) => ({ ...ch })) })) }))
  for (const { class: ref, ...option } of subclasses) {
    const id = option.id ?? slugify(option.name ?? '')
    const cls = out.find((c) => matchKey(c.id) === matchKey(ref ?? '') || matchKey(c.name) === matchKey(ref ?? ''))
    if (!cls) { errors.push(`Subclass '${option.name ?? id}': no class named '${ref}'.`); continue }
    const choice = cls.levels?.flatMap((l) => l.choices).find((c) => c.type === 'subclass')
    if (!choice) { errors.push(`Subclass '${option.name ?? id}': class '${cls.name}' has no subclass choice.`); continue }
    choice.options = [...(choice.options ?? []).filter((o) => o.id !== id), { ...option, id }]
  }
  return out
}

/** The six content arrays carried by a pack. */
const CONTENT_KEYS = [
  'species',
  'classes',
  'backgrounds',
  'spells',
  'equipment',
  'feats',
] as const
export type ContentKey = (typeof CONTENT_KEYS)[number]

/**
 * Map each resolved entity id back to the pack it came from, per content type.
 * Mirrors `resolveEntities` resolution order (an entity's id is its explicit
 * `id` or, failing that, the slugified `name`) and replays "later packs win"
 * (matching the `byId.set` overwrite order), so an extension that clones an SRD
 * base is attributed to the overriding pack.
 */
export function buildProvenance(packs: RawContentPack[]): Record<ContentKey | 'subclasses', Record<string, string>> {
  const result = {
    species: {},
    classes: {},
    subclasses: {},
    backgrounds: {},
    spells: {},
    equipment: {},
    feats: {},
  } as Record<ContentKey | 'subclasses', Record<string, string>>

  for (const pack of packs) {
    for (const key of CONTENT_KEYS) {
      for (const entry of pack[key] ?? []) {
        const e = entry as { id?: string; name?: string }
        const id = e.id ?? (e.name ? slugify(e.name) : undefined)
        if (id) result[key][id] = pack.id
      }
    }
    // A pack's subclasses for existing classes, by the option id they join the class under.
    for (const sub of pack.subclasses ?? []) {
      const id = sub.id ?? slugify(sub.name ?? '')
      if (id) result.subclasses[id] = pack.id
    }
  }

  return result
}
